import React, {useEffect, useState} from 'react';
import {useNavigate, useSearchParams} from 'react-router-dom';
import {Alert, Pagination, Space, Spin, Tag, Typography} from 'antd';

import NewsService from '../service/newsService';
import NewsItemCard from '../components/News/NewsItemCard';
import {fromUnifiedCard, UnifiedCard} from '../types/cardData';
// Removed useWindowScrollRestoration to avoid conflicts with NewsList state management
import '../styles/NewsDetail.css';

const { Title, Text } = Typography;

const TagSearchPage: React.FC = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [articles, setArticles] = useState<UnifiedCard[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [pagination, setPagination] = useState({
        page: 1,
        pageSize: 10,
        total: 0,
        totalPages: 0,
    });

    // Scroll restoration is handled by NewsList component's state management

    const tag = searchParams.get('tag') || '';

    useEffect(() => {
        const fetchTagResults = async () => {
            if (!tag) {
                setError('No tag specified');
                setLoading(false);
                return;
            }

            setLoading(true);
            setError(null);

            try {
                const response = await NewsService.searchNewsByTagsV2(
                    tag,
                    pagination.page,
                    pagination.pageSize
                );

                setArticles(response.data.data);
                setPagination({
                    page: response.data.page,
                    pageSize: response.data.pageSize,
                    total: response.data.total,
                    totalPages: response.data.totalPages,
                });
            } catch (err: any) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchTagResults();
    }, [tag, pagination.page, pagination.pageSize]);

    const handlePageChange = (page: number) => {
        setPagination(prev => ({ ...prev, page }));
    };

    const handleBack = () => {
        navigate(-1);
    };

    if (loading) {
        return (
            <div style={{ padding: '24px', textAlign: 'center' }}>
                <Spin size="large" />
                <div style={{ marginTop: '16px' }}>
                    <Text>Searching for articles with tag: "{tag}"</Text>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div style={{ padding: '24px', maxWidth: '800px', margin: '0 auto' }}>
                <Alert
                    message="Error"
                    description={error}
                    type="error"
                    showIcon
                    action={
                        <button onClick={handleBack} style={{ marginLeft: '8px' }}>
                            Go Back
                        </button>
                    }
                />
            </div>
        );
    }

    return (
        <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
                {/* Header */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div>
                        <Title level={2} style={{ margin: 0 }}>
                            Articles tagged with: <Tag color="blue" style={{ fontSize: '18px', padding: '4px 12px' }}>#{tag}</Tag>
                        </Title>
                        <Text type="secondary">
                            Found {pagination.total} article{pagination.total !== 1 ? 's' : ''}
                        </Text>
                    </div>
                    <button onClick={handleBack} style={{ padding: '8px 16px' }}>
                        ← Back
                    </button>
                </div>

                {/* Results */}
                {articles.length > 0 ? (
                    <div>
                        <div style={{ marginBottom: '24px' }}>
                            {articles.map((article) => (
                                <NewsItemCard key={article.id} data={fromUnifiedCard(article)} />
                            ))}
                        </div>

                        {/* Pagination */}
                        {pagination.totalPages > 1 && (
                            <div style={{ textAlign: 'center', marginTop: '24px' }}>
                                <Pagination
                                    current={pagination.page}
                                    total={pagination.total}
                                    pageSize={pagination.pageSize}
                                    showSizeChanger={false}
                                    onChange={handlePageChange}
                                    showQuickJumper
                                    showTotal={(total, range) =>
                                        `${range[0]}-${range[1]} of ${total} articles`
                                    }
                                />
                            </div>
                        )}
                    </div>
                ) : (
                    <div style={{ textAlign: 'center', padding: '48px 0' }}>
                        <Title level={3} type="secondary">
                            No articles found with tag "{tag}"
                        </Title>
                        <Text type="secondary">
                            Try searching for a different tag or browse our latest articles.
                        </Text>
                    </div>
                )}
            </Space>
        </div>
    );
};

export default TagSearchPage;
